/**
 * Deposits Service
 *
 * Proxies deposit actions (release / capture / refund) through the admin-panel's
 * api/stripe/deposit-action.js endpoint, which forwards to the customer-frontend's
 * deposit state machine.
 */

export type DepositAction = 'release' | 'capture' | 'refund';

export interface DepositActionResult {
  action: string;
  message: string;
  amount_captured?: number;
  amount_refunded?: number;
}

export async function performDepositAction(params: {
  bookingId: string;
  action: DepositAction;
  amount?: number;
  userId?: string;
}): Promise<{ data: DepositActionResult | null; error: string | null }> {
  try {
    const response = await fetch('/api/stripe/deposit-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      const errorMsg = result.message || result.error || `Deposit ${params.action} failed`;

      // Treat "already in target state" as a success (double-click safety)
      if (response.status === 400 && errorMsg.includes('Cannot') && errorMsg.includes('deposit in status')) {
        return {
          data: { action: params.action, message: errorMsg },
          error: null,
        };
      }

      return { data: null, error: errorMsg };
    }

    return {
      data: {
        action: result.action || params.action,
        message: result.message || `Deposit ${params.action} succeeded`,
        amount_captured: result.amount_captured,
        amount_refunded: result.amount_refunded,
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'Network error' };
  }
}
